import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Layout } from "../components/layout/Layout";
import { ProductCard } from "../components/common/ProductCard";
import { ProductLightbox } from "../components/common/ProductLightbox";
import { HiX, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { FaCheck, FaGamepad, FaMobileAlt, FaDesktop, FaGlobe } from "react-icons/fa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAIN_CATEGORIES = [
  { key: "cabinet", label: "Cabinets", subs: [] },
  { key: "kiosk", label: "Kiosks", subs: [
    { key: "ticket_redemption", label: "Ticket Redemption" },
    { key: "mobile_game", label: "Mobile Game" },
  ]},
  { key: "arcade", label: "Arcade", subs: [
    { key: "coin_pusher", label: "Coin Pushers" },
    { key: "claw_machine", label: "Claw Machines" },
  ]},
  { key: "gameboard", label: "Gameboards", subs: [
    { key: "all", label: "All" },
    { key: "banilla", label: "Banilla Games" },
    { key: "jenka", label: "Jenka Labs" },
    { key: "primero", label: "Primero Games" },
  ]},
  { key: "gaming_system", label: "Gaming Systems", subs: [] },
];

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState({});
  const [gameboards, setGameboards] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeCategory = searchParams.get("cat") || "cabinet";
  const activeSub = searchParams.get("sub") || "";

  const setCategory = (cat) => {
    setSelected(null);
    const params = { cat };
    const catObj = MAIN_CATEGORIES.find(c => c.key === cat);
    if (catObj?.subs?.length > 0 && cat !== "gameboard") {
      params.sub = catObj.subs[0].key;
    }
    if (cat === "gameboard") params.sub = "all";
    setSearchParams(params);
  };

  const setSub = (sub) => {
    setSelected(null);
    setSearchParams({ cat: activeCategory, sub });
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [cabRes, kioskRes, arcadeRes, gsRes, gbRes] = await Promise.all([
          axios.get(`${API}/products?category=cabinet`),
          axios.get(`${API}/products?category=kiosk`),
          axios.get(`${API}/products?category=arcade`),
          axios.get(`${API}/products?category=gaming_system`),
          axios.get(`${API}/gameboards`),
        ]);
        setAllProducts({
          cabinet: cabRes.data,
          kiosk: kioskRes.data,
          arcade: arcadeRes.data,
          gaming_system: gsRes.data,
        });
        setGameboards(gbRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  // Get filtered products for current view
  const getFiltered = () => {
    if (activeCategory === "gameboard") {
      if (!activeSub || activeSub === "all") return gameboards;
      return gameboards.filter(g => g.brand === activeSub);
    }
    const items = allProducts[activeCategory] || [];
    const catObj = MAIN_CATEGORIES.find(c => c.key === activeCategory);
    if (catObj?.subs?.length > 0 && activeSub) {
      return items.filter(p => (p.subcategory || catObj.subs[0].key) === activeSub);
    }
    return items;
  };

  const filtered = getFiltered();
  const currentIndex = selected ? filtered.findIndex(p => p.id === selected.id) : -1;
  const catObj = MAIN_CATEGORIES.find(c => c.key === activeCategory);

  const seoTitles = {
    cabinet: "Skill Game Cabinets | Nova Series",
    kiosk: "Gaming Kiosks | Ticket Redemption & Mobile Game",
    arcade: "Arcade Machines | Coin Pushers & Claw Machines",
    gameboard: "Skill Game Boards | Banilla, Jenka, Primero",
    gaming_system: "Online Gaming Systems | Sweepstakes Platforms",
  };

  const pageTitle = (seoTitles[activeCategory] || "Products") + " | Crown Amusement";

  return (
    <Layout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Browse our complete product catalog: skill game cabinets, kiosks, arcade machines, gameboards, and online gaming systems. Crown Amusement - your one-stop gaming solutions partner." />
        <link rel="canonical" href="https://crownamusement.com/products" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-6 overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gold-900/10 via-obsidian to-obsidian" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-5 tracking-wider uppercase border border-gold-500/20">
              Product Catalog
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
              Our <span className="text-gold-gradient">Products</span>
            </h1>
            <p className="mt-4 text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Complete gaming solutions — from cabinets and kiosks to arcade machines and online platforms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Category Tabs */}
      <section className="bg-obsidian border-b border-white/5 sticky top-16 z-30 backdrop-blur-xl bg-obsidian/95">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide" data-testid="product-category-tabs">
            {MAIN_CATEGORIES.map((cat) => {
              const count = cat.key === "gameboard"
                ? gameboards.length
                : (allProducts[cat.key] || []).length;
              return (
                <button
                  key={cat.key}
                  onClick={() => setCategory(cat.key)}
                  data-testid={`product-tab-${cat.key}`}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeCategory === cat.key
                      ? "border-gold-500 text-gold-400"
                      : "border-transparent text-gray-400 hover:text-white"
                  }`}
                >
                  {cat.label}
                  {!loading && count > 0 && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      activeCategory === cat.key ? "bg-gold-500/20 text-gold-400" : "bg-white/5 text-gray-500"
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Subcategory Tabs (if any) */}
      {catObj?.subs?.length > 0 && (
        <section className="bg-obsidian border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex gap-2 py-3 overflow-x-auto" data-testid="product-subcategory-tabs">
              {catObj.subs.map((sub) => (
                <button
                  key={sub.key}
                  onClick={() => setSub(sub.key)}
                  data-testid={`product-sub-${sub.key}`}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    activeSub === sub.key
                      ? "bg-gold-500 text-obsidian"
                      : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gaming Systems special layout */}
      {activeCategory === "gaming_system" ? (
        <GamingSystemsGrid items={filtered} loading={loading} />
      ) : activeCategory === "gameboard" ? (
        <GameboardsGrid
          items={filtered}
          loading={loading}
          selected={selected}
          setSelected={setSelected}
          currentIndex={currentIndex}
        />
      ) : (
        /* Standard grid for cabinets, kiosks, arcade */
        <section className="py-10 bg-obsidian">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-8">
              <p className="text-gray-500 text-xs">
                Showing <span className="text-gold-400 font-semibold">{filtered.length}</span> products
              </p>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-surface animate-pulse" />)}
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div key={`${activeCategory}-${activeSub}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="products-grid">
                  {filtered.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} onClick={() => setSelected(product)} />
                  ))}
                </motion.div>
              </AnimatePresence>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-20"><p className="text-gray-400 text-lg">No products found.</p></div>
            )}
          </div>
        </section>
      )}

      {/* Lightbox (for cabinets/kiosks/arcade) */}
      {selected && activeCategory !== "gameboard" && activeCategory !== "gaming_system" && (
        <ProductLightbox product={selected} onClose={() => setSelected(null)}
          onPrev={() => { if (currentIndex > 0) setSelected(filtered[currentIndex - 1]); }}
          onNext={() => { if (currentIndex < filtered.length - 1) setSelected(filtered[currentIndex + 1]); }}
          hasPrev={currentIndex > 0} hasNext={currentIndex < filtered.length - 1} />
      )}
    </Layout>
  );
};

/* Gaming Systems sub-component */
const GamingSystemsGrid = ({ items, loading }) => {
  const [selectedSystem, setSelectedSystem] = useState(null);
  return (
    <section className="py-10 bg-obsidian">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {[
            { icon: FaGlobe, label: "Cloud-Based" },
            { icon: FaMobileAlt, label: "Mobile Apps" },
            { icon: FaDesktop, label: "Browser Play" },
            { icon: FaGamepad, label: "Kiosk Compatible" },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs text-gray-300">
              <Icon className="text-gold-500" /> {label}
            </span>
          ))}
        </div>
        <div className="text-center mb-8">
          <p className="text-gray-500 text-xs">Showing <span className="text-gold-400 font-semibold">{items.length}</span> gaming systems</p>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {[...Array(10)].map((_, i) => <div key={i} className="aspect-square rounded-2xl bg-surface animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5" data-testid="gaming-systems-grid">
              {items.map((sys, index) => (
                <motion.div key={sys.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.03 }} viewport={{ once: true }}
                  onClick={() => setSelectedSystem(selectedSystem?.id === sys.id ? null : sys)}
                  className={`group cursor-pointer rounded-2xl overflow-hidden bg-surface border transition-all duration-300 ${
                    selectedSystem?.id === sys.id ? "border-gold-500 ring-1 ring-gold-500/30 scale-[1.02]" : "border-white/5 hover:border-gold-500/30"
                  }`} data-testid={`gaming-system-${sys.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="aspect-square overflow-hidden bg-charcoal">
                    <img src={sys.image_url} alt={sys.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-3 text-center">
                    <h3 className="font-heading text-sm text-white group-hover:text-gold-400 transition-colors truncate">{sys.name}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
            {selectedSystem && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 p-6 md:p-8 rounded-2xl bg-surface border border-gold-500/20" data-testid="gaming-system-detail">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="flex items-center justify-center">
                    <img src={selectedSystem.image_url} alt={selectedSystem.name} className="w-48 h-48 object-cover rounded-xl" />
                  </div>
                  <div className="md:col-span-2">
                    <h2 className="font-heading text-2xl text-white mb-3">{selectedSystem.name}</h2>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">{selectedSystem.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedSystem.features.map((feat, i) => (
                        <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-gold-500/10 text-gold-400 text-xs rounded-full">
                          <FaCheck className="text-[9px]" /> {feat}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

/* Gameboards sub-component with brand-colored cards and custom lightbox */
const GameboardsGrid = ({ items, loading, selected, setSelected, currentIndex }) => {
  return (
    <>
      <section className="py-10 bg-obsidian">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <p className="text-gray-500 text-xs">Showing <span className="text-gold-400 font-semibold">{items.length}</span> gameboards</p>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-surface animate-pulse" />)}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key="gameboards" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="gameboards-grid">
                {items.map((gb, index) => (
                  <motion.div key={gb.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.05 }} viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-2xl bg-surface border border-white/5 hover:border-gold-500/50 transition-all duration-500 cursor-pointer"
                    onClick={() => setSelected(gb)} data-testid={`gameboard-card-${gb.id}`}>
                    <div className="absolute top-4 left-4 z-10">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${
                        gb.brand === "banilla" ? "bg-yellow-500/20 text-yellow-400" : gb.brand === "jenka" ? "bg-purple-500/20 text-purple-400" : "bg-green-500/20 text-green-400"
                      }`}>{gb.brand === "banilla" ? "Banilla" : gb.brand === "jenka" ? "Jenka" : "Primero"}</span>
                    </div>
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img src={gb.image_url} alt={gb.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-heading text-xl text-white group-hover:text-gold-400 transition-colors duration-300">{gb.name}</h3>
                      <p className="mt-2 text-gray-400 text-sm line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">{gb.description}</p>
                    </div>
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-t from-gold-500/10 to-transparent" />
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
          {!loading && items.length === 0 && (
            <div className="text-center py-20"><p className="text-gray-400 text-lg">No gameboards found.</p></div>
          )}
        </div>
      </section>

      {/* Gameboard Lightbox */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" onClick={() => setSelected(null)} data-testid="gameboard-lightbox">
            <div className="absolute inset-0 bg-obsidian/95 backdrop-blur-xl" />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} transition={{ type: "spring", damping: 25 }}
              className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Close">
                <HiX className="text-white text-2xl" />
              </button>
              <div className="flex flex-col md:flex-row bg-charcoal rounded-2xl overflow-hidden border border-white/10">
                <div className="relative md:w-1/2">
                  <img src={selected.image_url} alt={selected.name} className="w-full h-64 md:h-full object-cover" />
                  {currentIndex > 0 && (
                    <button onClick={() => setSelected(items[currentIndex - 1])} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-obsidian/80 hover:bg-gold-500/20 transition-colors border border-white/10">
                      <HiChevronLeft className="text-white text-2xl" />
                    </button>
                  )}
                  {currentIndex < items.length - 1 && (
                    <button onClick={() => setSelected(items[currentIndex + 1])} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-obsidian/80 hover:bg-gold-500/20 transition-colors border border-white/10">
                      <HiChevronRight className="text-white text-2xl" />
                    </button>
                  )}
                </div>
                <div className="md:w-1/2 p-8 md:p-10 overflow-y-auto max-h-[60vh] md:max-h-[80vh]">
                  <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full capitalize mb-4 ${
                    selected.brand === "banilla" ? "bg-yellow-500/20 text-yellow-400" : selected.brand === "jenka" ? "bg-purple-500/20 text-purple-400" : "bg-green-500/20 text-green-400"
                  }`}>{selected.brand === "banilla" ? "Banilla Games" : selected.brand === "jenka" ? "Jenka Labs" : "Primero Games"}</span>
                  <h2 className="font-heading text-3xl md:text-4xl text-white">{selected.name}</h2>
                  <p className="text-gray-400 mt-4 leading-relaxed">{selected.description}</p>
                  {selected.features?.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-lg text-white mb-4">Features</h3>
                      <ul className="space-y-3">
                        {selected.features.map((f, i) => (
                          <li key={i} className="flex items-center gap-3 text-gray-300">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gold-500/20 flex items-center justify-center"><FaCheck className="text-gold-500 text-xs" /></span>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-8 flex flex-col sm:flex-row gap-4">
                    <a href="/contact" className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-semibold rounded-full hover:scale-105 transition-transform">Inquire Now</a>
                    <a href="tel:+18482198830" className="inline-flex items-center justify-center px-8 py-3 border border-gold-500/30 text-gold-400 font-medium rounded-full hover:bg-gold-500/10 transition-colors">Call Us</a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Products;
